/**
 * MealPlanEditorDialog — Phase 2.2B Session 4
 *
 * Backend: POST /api/nutrition/meal-plans/version  (always creates a NEW version; the
 * previous version stays in history). Server computes the daily totals from the foods
 * list. We compute a local preview so the nutritionist sees the running sum while
 * building the plan, but the server's `totals` is the source of truth on save.
 *
 * RBAC: ADMIN + NUTRITIONIST + SUPERADMIN (gated upstream).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  X, Loader2, Save, Plus, Trash2, Utensils, Sparkles, GripVertical,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const PLAN_TYPES = [
  { id: 'GENERIC', label_it: 'Generico', label_en: 'Generic' },
  { id: 'REST', label_it: 'Riposo', label_en: 'Rest' },
  { id: 'TRAINING', label_it: 'Allenamento', label_en: 'Training' },
  { id: 'MATCH', label_it: 'Partita', label_en: 'Match' },
  { id: 'DOUBLE_SESSION', label_it: 'Doppia seduta', label_en: 'Double session' },
  { id: 'RTP', label_it: 'RTP', label_en: 'RTP' },
];

const MEAL_SLOTS = [
  'BREAKFAST', 'SNACK_AM', 'LUNCH', 'SNACK_PM',
  'PRE_TRAINING', 'POST_TRAINING', 'DINNER', 'SNACK_EVENING',
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const toNum = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));
const fmt0 = (v) => v === null || v === undefined ? '—' : Math.round(Number(v));
const fmt1 = (v) => v === null || v === undefined ? '—' : Number(v).toFixed(1);

const emptyFood = () => ({ name: '', qty_g: '', kcal: '', protein_g: '', carbs_g: '', fats_g: '', notes: '' });

const MealPlanEditorDialog = ({
  open, onClose, athleteId, athleteName,
  activeTarget,            // for "Use active target" prefill + delta preview
  seedFromVersion,         // optional: prefill from an existing version (clone-edit-save)
  onSaved,
}) => {
  const { api } = useAuth();
  const { language } = useLanguage();
  const [planType, setPlanType] = useState('GENERIC');
  const [label, setLabel] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [effectiveTo, setEffectiveTo] = useState('');
  const [notes, setNotes] = useState('');
  const [meals, setMeals] = useState([{ slot: 'BREAKFAST', foods: [emptyFood()], meal_notes: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (seedFromVersion) {
      setPlanType(seedFromVersion.plan_type || 'GENERIC');
      setLabel(seedFromVersion.label || '');
      setEffectiveFrom(seedFromVersion.effective_from || todayIso());
      setEffectiveTo(seedFromVersion.effective_to || '');
      setNotes(seedFromVersion.notes || '');
      setMeals(
        (seedFromVersion.meals || []).map((m) => ({
          slot: m.slot,
          meal_notes: m.meal_notes || '',
          foods: (m.foods || []).map((f) => ({
            name: f.name || '',
            qty_g: f.qty_g ?? '',
            kcal: f.kcal ?? '',
            protein_g: f.protein_g ?? '',
            carbs_g: f.carbs_g ?? '',
            fats_g: f.fats_g ?? '',
            notes: f.notes || '',
          })),
        }))
      );
    } else {
      setPlanType('GENERIC'); setLabel(''); setEffectiveFrom(todayIso()); setEffectiveTo('');
      setNotes('');
      setMeals([{ slot: 'BREAKFAST', foods: [emptyFood()], meal_notes: '' }]);
    }
    setError(null);
  }, [open, seedFromVersion]);

  // Live totals — mirrors backend aggregate_meal_totals
  const totals = useMemo(() => {
    let kcal = 0, p = 0, c = 0, f = 0;
    meals.forEach((m) => (m.foods || []).forEach((fo) => {
      kcal += toNum(fo.kcal) || 0;
      p += toNum(fo.protein_g) || 0;
      c += toNum(fo.carbs_g) || 0;
      f += toNum(fo.fats_g) || 0;
    }));
    return { calories: kcal, protein_g: p, carbs_g: c, fats_g: f };
  }, [meals]);

  const deltaVsTarget = useMemo(() => {
    if (!activeTarget?.final) return null;
    const t = activeTarget.final;
    return {
      calories: totals.calories - (t.calories || 0),
      protein_g: totals.protein_g - (t.protein_g || 0),
      carbs_g: totals.carbs_g - (t.carbs_g || 0),
      fats_g: totals.fats_g - (t.fats_g || 0),
    };
  }, [totals, activeTarget]);

  if (!open) return null;

  const handleUseActiveTarget = () => {
    if (!activeTarget?.final) return;
    // A "prefill" cannot literally fill foods — but it offers a single sentinel
    // "Target placeholder" food so the running totals start at the target. The
    // nutritionist then iterates by replacing/editing food rows.
    const t = activeTarget.final;
    setMeals((prev) => [
      ...prev,
      {
        slot: 'BREAKFAST',
        meal_notes: language === 'IT' ? 'Da Target attivo — modifica al volo' : 'From active target — edit inline',
        foods: [{
          name: language === 'IT' ? 'Pasto base (da target)' : 'Base meal (from target)',
          qty_g: 0, kcal: t.calories || 0, protein_g: t.protein_g || 0,
          carbs_g: t.carbs_g || 0, fats_g: t.fats_g || 0, notes: '',
        }],
      },
    ]);
  };

  const updateMeal = (idx, patch) => setMeals((prev) => prev.map((m, i) => i === idx ? { ...m, ...patch } : m));
  const removeMeal = (idx) => setMeals((prev) => prev.filter((_, i) => i !== idx));
  const addMeal = () => {
    const used = new Set(meals.map((m) => m.slot));
    const next = MEAL_SLOTS.find((s) => !used.has(s)) || 'SNACK_AM';
    setMeals([...meals, { slot: next, foods: [emptyFood()], meal_notes: '' }]);
  };
  const addFood = (mIdx) => updateMeal(mIdx, { foods: [...meals[mIdx].foods, emptyFood()] });
  const updateFood = (mIdx, fIdx, patch) => {
    const newFoods = meals[mIdx].foods.map((f, i) => i === fIdx ? { ...f, ...patch } : f);
    updateMeal(mIdx, { foods: newFoods });
  };
  const removeFood = (mIdx, fIdx) => {
    const newFoods = meals[mIdx].foods.filter((_, i) => i !== fIdx);
    updateMeal(mIdx, { foods: newFoods });
  };

  const handleSave = async () => {
    setError(null);
    // Validate at least 1 food with required fields
    const cleanMeals = [];
    for (const m of meals) {
      const cleanFoods = m.foods
        .filter((f) => (f.name || '').trim().length > 0)
        .map((f) => ({
          name: f.name.trim(),
          qty_g: toNum(f.qty_g) ?? 0,
          kcal: toNum(f.kcal) ?? 0,
          protein_g: toNum(f.protein_g) ?? 0,
          carbs_g: toNum(f.carbs_g) ?? 0,
          fats_g: toNum(f.fats_g) ?? 0,
          ...(f.notes ? { notes: f.notes } : {}),
        }));
      if (cleanFoods.length > 0) {
        cleanMeals.push({ slot: m.slot, foods: cleanFoods, ...(m.meal_notes ? { meal_notes: m.meal_notes } : {}) });
      }
    }
    if (cleanMeals.length === 0) {
      setError(language === 'IT' ? 'Aggiungi almeno un alimento.' : 'Add at least one food.');
      return;
    }
    // Backend requires qty_g > 0 for each food
    for (const m of cleanMeals) for (const f of m.foods) {
      if (f.qty_g <= 0) {
        setError(language === 'IT' ? `Quantità (g) > 0 richiesta per "${f.name}"` : `Quantity (g) > 0 required for "${f.name}"`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        athlete_id: athleteId,
        label: label || null,
        plan_type: planType,
        effective_from: effectiveFrom,
        effective_to: effectiveTo || null,
        target_id: activeTarget?.id || null,
        meals: cleanMeals,
        notes: notes || null,
      };
      const res = await api.post('/nutrition/meal-plans/version', payload);
      onSaved && onSaved(res.data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map((d) => d.msg).join('; ') : (detail || (language === 'IT' ? 'Salvataggio fallito' : 'Save failed')));
    } finally {
      setSaving(false);
    }
  };

  const lbl = (o) => language === 'IT' ? o.label_it : o.label_en;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="mealplan-editor-dialog">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-5xl max-h-[94vh] overflow-y-auto m-4">
        <header className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Utensils className="w-5 h-5 text-amber-400" />
            <div>
              <h2 className="text-lg font-semibold text-zinc-100">
                {language === 'IT' ? 'Nuova versione piano alimentare' : 'New meal plan version'}
              </h2>
              <p className="text-xs text-zinc-500 mt-0.5">{athleteName}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="mealplan-editor-close">
            <X className="w-4 h-4" />
          </Button>
        </header>

        <div className="p-6 space-y-5">
          {/* Plan type + dates + label */}
          <section className="bg-zinc-950/50 border border-zinc-800 rounded p-4" data-testid="mealplan-meta">
            <h3 className="text-xs uppercase tracking-wider text-zinc-400 mb-3">
              {language === 'IT' ? 'Tipo di piano & validità' : 'Plan type & validity'}
            </h3>
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="mealplan-type-selector">
              {PLAN_TYPES.map((p) => (
                <button
                  key={p.id} type="button"
                  onClick={() => setPlanType(p.id)}
                  data-testid={`mealplan-type-${p.id}`}
                  className={`px-3 py-1.5 text-xs rounded border transition ${
                    planType === p.id
                      ? 'border-amber-500 bg-amber-500/10 text-amber-200'
                      : 'border-zinc-800 hover:border-zinc-600 text-zinc-300'
                  }`}
                >{lbl(p)}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <label className="text-[10px] uppercase text-zinc-500">{language === 'IT' ? 'Etichetta' : 'Label'}</label>
                <input
                  value={label} onChange={(e) => setLabel(e.target.value)}
                  placeholder={language === 'IT' ? 'es. v3 pre-stagione' : 'e.g. v3 pre-season'}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-amber-600"
                  data-testid="mealplan-label"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase text-zinc-500">{language === 'IT' ? 'Valido dal' : 'Effective from'}</label>
                <input
                  type="date" value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 focus:outline-none focus:border-amber-600"
                  data-testid="mealplan-effective-from"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase text-zinc-500">{language === 'IT' ? 'Valido al' : 'Effective to'}</label>
                <input
                  type="date" value={effectiveTo}
                  onChange={(e) => setEffectiveTo(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 focus:outline-none focus:border-amber-600"
                  data-testid="mealplan-effective-to"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={handleUseActiveTarget}
                  disabled={!activeTarget?.final}
                  data-testid="mealplan-use-active-target"
                  className="border-cyan-700 text-cyan-300 hover:bg-cyan-500/10 w-full"
                  title={!activeTarget?.final ? (language === 'IT' ? 'Nessun target attivo' : 'No active target') : ''}
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                  {language === 'IT' ? 'Usa target attivo' : 'Use active target'}
                </Button>
              </div>
            </div>
          </section>

          {/* Meals builder */}
          <section className="space-y-3" data-testid="mealplan-meals-builder">
            <div className="flex items-center justify-between">
              <h3 className="text-xs uppercase tracking-wider text-zinc-400">
                {language === 'IT' ? 'Pasti' : 'Meals'} ({meals.length})
              </h3>
              <Button size="sm" variant="outline" onClick={addMeal} data-testid="mealplan-add-meal">
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                {language === 'IT' ? 'Aggiungi pasto' : 'Add meal'}
              </Button>
            </div>

            {meals.map((m, mIdx) => (
              <MealCard
                key={mIdx} m={m} mIdx={mIdx}
                onSlotChange={(slot) => updateMeal(mIdx, { slot })}
                onNotesChange={(meal_notes) => updateMeal(mIdx, { meal_notes })}
                onAddFood={() => addFood(mIdx)}
                onUpdateFood={(fIdx, patch) => updateFood(mIdx, fIdx, patch)}
                onRemoveFood={(fIdx) => removeFood(mIdx, fIdx)}
                onRemoveMeal={() => removeMeal(mIdx)}
                language={language}
              />
            ))}
          </section>

          {/* Live totals + delta */}
          <section className="bg-zinc-950 border border-zinc-800 rounded p-4" data-testid="mealplan-totals">
            <h3 className="text-xs uppercase tracking-wider text-zinc-400 mb-3">
              {language === 'IT' ? 'Totali del piano' : 'Plan totals'} {activeTarget?.final && `· vs target`}
            </h3>
            <div className="grid grid-cols-4 gap-3">
              <TotalCell testid="totals-calories" label={language === 'IT' ? 'Calorie' : 'Calories'} value={`${fmt0(totals.calories)} kcal`} delta={deltaVsTarget?.calories} />
              <TotalCell testid="totals-protein" label={language === 'IT' ? 'Proteine' : 'Protein'} value={`${fmt1(totals.protein_g)} g`} delta={deltaVsTarget?.protein_g} />
              <TotalCell testid="totals-carbs" label={language === 'IT' ? 'Carboidrati' : 'Carbs'} value={`${fmt1(totals.carbs_g)} g`} delta={deltaVsTarget?.carbs_g} />
              <TotalCell testid="totals-fats" label={language === 'IT' ? 'Grassi' : 'Fats'} value={`${fmt1(totals.fats_g)} g`} delta={deltaVsTarget?.fats_g} />
            </div>
          </section>

          {/* Notes */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500">{language === 'IT' ? 'Note del piano' : 'Plan notes'}</label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-amber-600 resize-none"
              data-testid="mealplan-notes"
              placeholder={language === 'IT' ? 'Indicazioni generali, timing supplementi…' : 'General guidance, supplement timing…'}
            />
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="mealplan-editor-error">{error}</div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-zinc-900 border-t border-zinc-800 px-6 py-3 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="mealplan-editor-cancel">
            {language === 'IT' ? 'Annulla' : 'Cancel'}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="mealplan-editor-save"
            className="bg-amber-600 hover:bg-amber-500 text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {language === 'IT' ? 'Salva nuova versione' : 'Save new version'}
          </Button>
        </footer>
      </div>
    </div>
  );
};

const TotalCell = ({ label, value, delta, testid }) => {
  let deltaColor = 'text-zinc-500';
  if (delta !== null && delta !== undefined) {
    if (delta > 5) deltaColor = 'text-amber-400';
    else if (delta < -5) deltaColor = 'text-cyan-400';
    else deltaColor = 'text-emerald-400';
  }
  return (
    <div className="bg-zinc-900 rounded p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className="text-lg font-bold text-zinc-100">{value}</div>
      {delta !== null && delta !== undefined && (
        <div className={`text-[11px] mt-0.5 ${deltaColor}`} data-testid={`${testid}-delta`}>
          {delta > 0 ? '+' : ''}{Number(delta).toFixed(1)} vs target
        </div>
      )}
    </div>
  );
};

const MealCard = ({ m, mIdx, onSlotChange, onNotesChange, onAddFood, onUpdateFood, onRemoveFood, onRemoveMeal, language }) => (
  <div className="bg-zinc-950/40 border border-zinc-800 rounded p-3" data-testid={`meal-card-${mIdx}`}>
    <div className="flex items-center gap-2 mb-2">
      <GripVertical className="w-4 h-4 text-zinc-600" />
      <select
        value={m.slot}
        onChange={(e) => onSlotChange(e.target.value)}
        className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-200 focus:outline-none focus:border-amber-600 font-medium uppercase tracking-wider"
        data-testid={`meal-slot-${mIdx}`}
      >
        {MEAL_SLOTS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
      </select>
      <input
        type="text" value={m.meal_notes || ''}
        onChange={(e) => onNotesChange(e.target.value)}
        placeholder={language === 'IT' ? 'Note pasto (opz.)' : 'Meal notes (opt.)'}
        className="flex-1 bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-amber-600"
        data-testid={`meal-notes-${mIdx}`}
      />
      <Button size="sm" variant="ghost" onClick={onRemoveMeal} data-testid={`meal-remove-${mIdx}`}
        className="text-red-400 hover:bg-red-500/10 px-2">
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>

    <table className="w-full text-xs">
      <thead className="text-zinc-500">
        <tr>
          <th className="text-left px-1 py-1 font-normal">{language === 'IT' ? 'Alimento' : 'Food'}</th>
          <th className="text-right px-1 w-16 font-normal">g</th>
          <th className="text-right px-1 w-16 font-normal">kcal</th>
          <th className="text-right px-1 w-16 font-normal">P</th>
          <th className="text-right px-1 w-16 font-normal">C</th>
          <th className="text-right px-1 w-16 font-normal">F</th>
          <th className="w-8" />
        </tr>
      </thead>
      <tbody>
        {m.foods.map((f, fIdx) => (
          <tr key={fIdx} data-testid={`food-row-${mIdx}-${fIdx}`}>
            <td className="px-1 py-0.5">
              <input value={f.name} onChange={(e) => onUpdateFood(fIdx, { name: e.target.value })}
                placeholder={language === 'IT' ? 'es. Petto di pollo' : 'e.g. Chicken breast'}
                className="w-full bg-zinc-900 border border-zinc-800 rounded px-1.5 py-1 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-amber-600"
                data-testid={`food-name-${mIdx}-${fIdx}`} />
            </td>
            {['qty_g', 'kcal', 'protein_g', 'carbs_g', 'fats_g'].map((k) => (
              <td key={k} className="px-1 py-0.5">
                <input type="number" step="0.1" value={f[k] ?? ''}
                  onChange={(e) => onUpdateFood(fIdx, { [k]: e.target.value })}
                  className="w-full bg-zinc-900 border border-zinc-800 rounded px-1.5 py-1 text-xs text-zinc-200 focus:outline-none focus:border-amber-600 text-right"
                  data-testid={`food-${k}-${mIdx}-${fIdx}`} />
              </td>
            ))}
            <td className="px-1 py-0.5 text-right">
              <Button size="sm" variant="ghost" onClick={() => onRemoveFood(fIdx)}
                data-testid={`food-remove-${mIdx}-${fIdx}`}
                className="text-red-400 hover:bg-red-500/10 px-1.5 py-1 h-auto">
                <Trash2 className="w-3 h-3" />
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <Button size="sm" variant="ghost" onClick={onAddFood} data-testid={`food-add-${mIdx}`}
      className="text-zinc-400 hover:text-amber-300 text-xs mt-1 h-auto px-2 py-1">
      <Plus className="w-3 h-3 mr-1" />
      {language === 'IT' ? 'Aggiungi alimento' : 'Add food'}
    </Button>
  </div>
);

export default MealPlanEditorDialog;
